/**
 * usePaymentDetection — o interruptor "detectar pagamento automaticamente" e o
 * modo de QR resultante (estático × dinâmico).
 *
 * A regra de decisão inteira mora no módulo puro `src/lib/paymentDetection.ts`;
 * aqui só entra o I/O. Duas leituras, com contratos diferentes de propósito:
 *
 *   `payment_settings`  a ESCOLHA do usuário. Cacheada em localStorage, porque a
 *                       tela precisa mostrar a posição do interruptor sem rede.
 *   `asaas_accounts`    o estado da subconta. NUNCA cacheado: é o que autoriza
 *                       cobrar dinheiro de verdade, e um cache velho poderia
 *                       dizer "aprovada" depois de a conta ser desativada. Sem
 *                       leitura fresca o modo cai em estático com o motivo
 *                       `account_unknown`, que é a falha segura.
 *
 * **Gravar é online-only** (configuração, não dado de negócio — mesma decisão de
 * `useStockSettings` #91 e dos modelos de mensagem #35). A mutation ainda assim
 * declara `networkMode: 'offlineFirst'`, e é justamente por isso: com o default
 * `'online'` o TanStack PAUSARIA ao perder a rede com a tela aberta (#41) e o
 * aviso "exige conexão" nunca apareceria — o usuário veria "Salvando…" para
 * sempre. Aqui a opção serve para FALHAR RÁPIDO, não para enfileirar.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import {
  autoDetectionReachable,
  canToggleAutoDetection,
  resolvePixQrMode,
  type AsaasAccountStatus,
  type PaymentDetectionAccount,
  type PixQrModeResolution,
} from '@/lib/paymentDetection';

const SETTINGS_KEY = 'payment-settings';
const ACCOUNT_KEY = 'asaas-account';

const settingsCacheKey = (companyId: string) => `orcamigo_payment_settings_${companyId}`;

function readCachedEnabled(companyId: string | null): boolean | undefined {
  if (!companyId) return undefined;
  try {
    const raw = localStorage.getItem(settingsCacheKey(companyId));
    if (!raw) return undefined;
    return JSON.parse(raw)?.auto_detection_enabled === true;
  } catch {
    return undefined;
  }
}

function writeCachedEnabled(companyId: string, enabled: boolean): void {
  try {
    localStorage.setItem(settingsCacheKey(companyId), JSON.stringify({ auto_detection_enabled: enabled }));
  } catch {
    // Cota: a leitura degrada para `false` (desligado), que é o comportamento
    // anterior à feature. Nunca para "ligado".
  }
}

interface AccountRow {
  status: AsaasAccountStatus;
  payout_pix_key: string | null;
  pix_key_ready: boolean | null;
  environment: 'sandbox' | 'production';
}

export interface UsePaymentDetectionResult {
  /** A posição do interruptor. `false` para empresa sem linha na tabela. */
  enabled: boolean;
  /** Modo EFETIVO + motivo, já resolvido. É isto que o PDF e a tela consomem. */
  resolution: PixQrModeResolution;
  /** Atalho: o documento leva QR dinâmico (cobrança Asaas) em vez do estático. */
  dynamic: boolean;
  /** O usuário pode MEXER no interruptor agora (plano + papel + conexão). */
  canToggle: boolean;
  /** O bloco deve aparecer na tela? Portão de PAPEL, antes do de plano (#38). */
  visible: boolean;
  loading: boolean;
  toggling: boolean;
  setEnabled: (next: boolean) => Promise<unknown>;
}

export function usePaymentDetection(): UsePaymentDetectionResult {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance, canManageCompany } = useRole();
  const feature = useFeature('auto_payment_detection');
  const queryClient = useQueryClient();

  const companyId = activeCompany?.id ?? null;
  const ready = Boolean(user?.id && companyId);

  const settingsQuery = useQuery({
    queryKey: [SETTINGS_KEY, companyId],
    enabled: ready,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem),
    // então `refetchOnMount` já passou quando ela habilita: sem o carimbo, o
    // cache semeado conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => readCachedEnabled(companyId),
    queryFn: async (): Promise<boolean> => {
      if (!companyId) return false;
      const { data, error } = await supabase
        .from('payment_settings' as never)
        .select('auto_detection_enabled')
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      // Empresa sem linha → desligado. Nenhuma linha é criada na LEITURA: um
      // `queryFn` que semeia é a armadilha #37, e aqui ele criaria configuração
      // de pagamento para quem só abriu a tela.
      const row = data as unknown as { auto_detection_enabled?: boolean } | null;
      const enabled = row?.auto_detection_enabled === true;
      writeCachedEnabled(companyId, enabled);
      return enabled;
    },
  });

  const accountQuery = useQuery({
    queryKey: [ACCOUNT_KEY, companyId],
    enabled: ready,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    retry: 1,
    queryFn: async (): Promise<PaymentDetectionAccount | null> => {
      if (!companyId) return null;
      const { data, error } = await supabase
        .from('asaas_accounts' as never)
        .select('status, payout_pix_key, pix_key_ready, environment')
        .eq('company_id', companyId);
      if (error) throw error;

      const rows = (data as unknown as AccountRow[] | null) ?? [];
      if (rows.length === 0) return null;
      // A tabela é única por (empresa, AMBIENTE) e o ambiente de operação é uma
      // variável das Edge Functions — o bundle não a enxerga. Produção manda:
      // um usuário real só tem a linha de produção, e a de sandbox só existe na
      // empresa de teste do desenvolvedor. Preferir produção evita que a conta de
      // teste (aprovada em segundos, #165j) libere cobrança em produção.
      const row = rows.find((candidate) => candidate.environment === 'production') ?? rows[0];

      // MAPEAMENTO EXPLÍCITO, nunca um `as PaymentDetectionAccount` sobre a
      // linha crua: a coluna é `payout_pix_key` e o campo é `payoutPixKey`. O
      // cast compila, e `account.payoutPixKey` fica `undefined` para sempre —
      // a tela passa a dizer "falta informar a chave de repasse" para quem já
      // informou, e a detecção NUNCA sai do estático. Achado só no device.
      return {
        status: row.status,
        payoutPixKey: row.payout_pix_key,
        pixKeyReady: row.pix_key_ready,
      };
    },
  });

  const enabled = settingsQuery.data === true;

  const resolution = useMemo(
    () =>
      resolvePixQrMode({
        enabled,
        // `autoDetectionReachable`, e não `feature.allowed` cru: com o BaaS
        // congelado nenhuma cobrança pode nascer, então resolver `dynamic`
        // apagaria o QR estático do documento sem pôr nada no lugar. Ver o
        // porquê inteiro em `src/lib/paymentDetection.ts`.
        featureAllowed: autoDetectionReachable(feature.allowed),
        // `undefined` enquanto não SABEMOS (carregando, offline, erro) — o módulo
        // puro distingue isso de `null` (não existe) e a tela deixa de acusar
        // "falta conectar sua conta" para quem já conectou.
        account: accountQuery.isSuccess ? accountQuery.data ?? null : undefined,
      }),
    [enabled, feature.allowed, accountQuery.isSuccess, accountQuery.data],
  );

  const toggle = useMutation({
    networkMode: 'offlineFirst' as const,
    mutationFn: async (next: boolean) => {
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      if (!(await checkNetworkStatus())) throw new Error('offline');

      const { error } = await supabase
        .from('payment_settings' as never)
        .upsert(
          { company_id: companyId, auto_detection_enabled: next } as never,
          { onConflict: 'company_id' },
        );
      if (error) throw error;

      writeCachedEnabled(companyId, next);
      queryClient.setQueryData([SETTINGS_KEY, companyId], next);
      return next;
    },
    onSuccess: (next) => {
      toast.success(
        next
          ? 'Detecção automática de pagamento ligada.'
          : 'Detecção automática desligada. Seus orçamentos voltam ao QR da sua chave Pix.',
      );
    },
    onError: (error: Error) => {
      if (error?.message === 'offline') {
        toast.warning('Essa configuração exige conexão. Conecte-se e tente de novo.');
        return;
      }
      toast.error('Não foi possível salvar a configuração de pagamento.');
    },
  });

  return {
    enabled,
    resolution,
    dynamic: resolution.dynamic,
    // Portão de PAPEL antes do de plano (#38): quem não vê o financeiro não vê
    // este bloco, e mostrar upsell de PRO para ele seria mentira dupla — o
    // problema dele não é plano, e assinar não resolveria.
    // O `autoDetectionReachable` no fim é o congelamento do BaaS: sem ele o
    // bloco aparece para `admin`/override com um interruptor que, ligado, tira
    // o QR estático do documento e não põe cobrança nenhuma no lugar.
    visible: canSeeFinance && !feature.blocked && autoDetectionReachable(feature.allowed),
    canToggle: canToggleAutoDetection({
      featureAllowed: autoDetectionReachable(feature.allowed),
      canWrite: canManageCompany,
      isOnline: null,
    }),
    loading: settingsQuery.isLoading || accountQuery.isLoading,
    toggling: toggle.isPending,
    setEnabled: (next: boolean) => toggle.mutateAsync(next),
  };
}
